/* Sentry error tracking — opt-in via SENTRY_DSN below.
   ─────────────────────────────────────────────────────
   1) Sign up at https://sentry.io (free), create a project
      "javascript-browser" → copy the DSN (looks like
      "https://abc123@o12345.ingest.us.sentry.io/678").
   2) Paste it into SENTRY_DSN below.
   3) Deploy hosting. Errors appear in your Sentry dashboard
      within seconds of happening.

   The DSN is a public token — designed to be shipped to
   browsers — so committing it is fine. Don't commit any
   *auth tokens* or service-account keys; those are
   different beasts.

   The SDK is lazy-loaded from esm.sh so the main bundle
   isn't bloated for users who don't have errors. About
   ~30KB on first error.
*/

export const SENTRY_DSN = "https://bd4525fea9ed1093a0bb6b4a0b350b6b@o4511301169905664.ingest.us.sentry.io/4511301180456960"; // ← paste your DSN here to enable

let _ready = false;
let _Sentry = null;
let _sessionUser = null;

/** Did the admin actually wire a DSN? Called by callers that need to know
    whether tracking is functional (so they don't bother piling up events
    for a no-op sink). */
export function isErrorTrackingEnabled() {
  return typeof SENTRY_DSN === "string" && SENTRY_DSN.startsWith("https://");
}

/** Initialise Sentry. Safe to call multiple times — only the first call
    actually loads the SDK. Subsequent calls just update the user context. */
export async function initErrorTracking() {
  if (!isErrorTrackingEnabled()) return;
  if (_ready) return;
  try {
    _Sentry = await import("https://esm.sh/@sentry/browser@8");
    _Sentry.init({
      dsn: SENTRY_DSN,
      // Tag events with the SW cache version for forensics. Sourced from
      // sw.js so we don't have to maintain it in two places.
      release: await readSwVersion(),
      environment: location.hostname === "localhost" ? "dev" : "prod",

      // Performance: keep at 10% so we don't blow the free quota on
      // routine renders. Bump to 1.0 temporarily when investigating.
      tracesSampleRate: 0.1,

      // Don't capture noise that isn't actionable.
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "Non-Error promise rejection captured",
        // Firebase Auth's own popup-closed signals — we already handle them.
        "auth/popup-closed-by-user",
        "auth/cancelled-popup-request",
        "auth/popup-blocked",
        // Service worker update churn during deploys.
        "Failed to register a ServiceWorker",
      ],
      denyUrls: [
        // Anything injected by extensions
        /^chrome-extension:\/\//,
        /^moz-extension:\/\//,
        /^safari-web-extension:\/\//,
      ],

      beforeSend(event, hint) {
        // Strip auth-related URLs in breadcrumbs that may carry tokens.
        if (event.request && event.request.url) {
          event.request.url = scrubUrl(event.request.url);
        }
        if (event.breadcrumbs) {
          for (const b of event.breadcrumbs) {
            if (b.data && b.data.url) b.data.url = scrubUrl(b.data.url);
          }
        }
        return event;
      },
    });
    _ready = true;
    if (_sessionUser) _Sentry.setUser(_sessionUser);
    console.log("[sentry] ready");
  } catch (err) {
    // Don't let Sentry failures break the app — just log and move on.
    console.warn("[sentry] init failed:", err);
  }
}

/** Attach the signed-in user to subsequent events. Safe to call before
    init — the value is stashed and applied once the SDK loads.
    Accepts null/undefined for sign-out without throwing. */
export function setErrorUser(user) {
  _sessionUser = user && user.uid ? { id: user.uid, email: user.email } : null;
  if (_ready && _Sentry) _Sentry.setUser(_sessionUser);
}

/** Manually capture an exception (e.g., from a try/catch where you want
    the error to surface but the catch is also handling UX). */
export function captureError(err, ctx) {
  if (!_ready || !_Sentry) {
    // Drop silently if Sentry isn't ready — alternative would be to queue
    // and flush on init, but for our scale that's overkill.
    return;
  }
  _Sentry.captureException(err, { extra: ctx });
}

/** Add a breadcrumb so future errors carry the trail. Cheap; called from
    routers, button handlers, etc. */
export function trackEvent(message, data) {
  if (!_ready || !_Sentry) return;
  _Sentry.addBreadcrumb({ category: "app", message, data, level: "info" });
}

// ─── helpers ───────────────────────────────────────────────────────────
async function readSwVersion() {
  try {
    const res = await fetch("./sw.js", { cache: "no-cache" });
    if (!res.ok) return undefined;
    const text = await res.text();
    const m = text.match(/CACHE\s*=\s*"([^"]+)"/);
    return m ? m[1] : undefined;
  } catch { return undefined; }
}

function scrubUrl(url) {
  try {
    const u = new URL(url, location.origin);
    // Strip auth tokens in query strings (id_token, access_token, code).
    const sensitive = ["id_token", "access_token", "code", "state"];
    for (const k of sensitive) if (u.searchParams.has(k)) u.searchParams.set(k, "[redacted]");
    return u.toString();
  } catch { return url; }
}
